'use client';

import { useEffect, useRef } from 'react';
import { useIntersectionObserver } from 'hooks';
import { logger } from 'utils';
import type { VideoProps } from '../types';
import { VIDEO_INTERSECTION_OBSERVER_ROOT_MARGIN } from '../constants';
import '../styles.css';

/**
 * HTML5Video component with lazy loading and progressive enhancement support.
 *
 * Features:
 * - Lazy loading: Videos load when near viewport (200px margin, 10% threshold)
 * - Progressive enhancement: Critical videos work without JavaScript
 * - Performance optimized: Uses preload="none" for off-screen/click-to-play videos
 * - No CLS: Video element always rendered to prevent layout shifts
 * - Autoplay support: Conditionally autoplays based on visibility and loadInstantly prop
 * - Poster deferred: Poster image only set when video is about to load, preventing off-screen thumbnail fetches
 *
 * @param {VideoProps} props - Component props
 * @param {VideoNode} props.video - Video configuration including sources, autoPlay, loop, etc.
 * @param {boolean} [props.loadInstantly=false] - If true, video renders immediately (for above-the-fold videos)
 *
 * @example
 * // Above-the-fold video with autoplay (works without JS)
 * <HTML5Video video={{ ...videoData, autoPlay: true }} loadInstantly={true} />
 *
 * @example
 * // Below-the-fold video with lazy loading
 * <HTML5Video video={{ ...videoData, autoPlay: true }} loadInstantly={false} />
 *
 * @example
 * // Click-to-play video (no autoplay)
 * <HTML5Video video={{ ...videoData, autoPlay: false }} />
 *
 * Behavior Matrix:
 * ┌────────────────┬──────────┬────────────────┬─────────────────┬──────────────────┬──────────────┬────────────────────────────────────────┐
 * │ loadInstantly  │ autoPlay │ isIntersecting │ shouldAutoPlay  │ shouldPreload    │ poster       │ Behavior                               │
 * ├────────────────┼──────────┼────────────────┼─────────────────┼──────────────────┼──────────────┼────────────────────────────────────────┤
 * │ true           │ true     │ -              │ true            │ 'auto'           │ thumbnailUrl │ Loads & autoplays immediately (no JS)  │
 * │ true           │ false    │ -              │ false           │ 'none'           │ thumbnailUrl │ Renders but doesn't preload (saves BW) │
 * │ false          │ true     │ true           │ true            │ 'auto'           │ thumbnailUrl │ Lazy loads & autoplays in viewport     │
 * │ false          │ true     │ false          │ false           │ 'none'           │ ''           │ Doesn't load until near viewport       │
 * │ false          │ false    │ true           │ false           │ 'auto'           │ thumbnailUrl │ Loads in viewport, user clicks to play │
 * │ false          │ false    │ false          │ false           │ 'none'           │ ''           │ Doesn't load until visible             │
 * └────────────────┴──────────┴────────────────┴─────────────────┴──────────────────┴──────────────┴────────────────────────────────────────┘
 *
 * Technical notes:
 * - All videos use muted + playsInline to comply with browser autoplay policies
 * - Poster is only set when shouldLoadMedia (loadInstantly || isIntersecting) — browsers fetch poster regardless of preload="none"
 * - Intersection Observer has 200px rootMargin and 0.1 threshold
 */
export function HTML5Video(props: VideoProps) {
  const {
    video: { autoPlay, thumbnailUrl, loop, sources, caption },
    loadInstantly = false,
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);

  const { ref: containerRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
    rootMargin: VIDEO_INTERSECTION_OBSERVER_ROOT_MARGIN,
    threshold: 0.1,
  });

  const shouldLoadMedia = loadInstantly || isIntersecting;
  const shouldAutoPlay = loadInstantly ? autoPlay : autoPlay && isIntersecting;
  const shouldPreload = (loadInstantly && autoPlay) || isIntersecting ? 'auto' : 'none';
  const shouldLoop = loop > 1;

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !autoPlay) return;

    // loadInstantly videos use the HTML autoPlay attribute (works without JS)
    if (loadInstantly) return;

    // Lazy-loaded videos: manually trigger play() when intersecting
    // (shouldAutoPlay is false until isIntersecting becomes true)
    if (isIntersecting) {
      video.play().catch(error => logger.error({ error }, 'Video autoplay failed'));
    }
  }, [autoPlay, isIntersecting, loadInstantly]);

  const videoCaption = caption || 'Video';

  return (
    <div className="video-container" ref={containerRef}>
      <video
        ref={videoRef}
        autoPlay={shouldAutoPlay}
        aria-label={videoCaption}
        height="auto"
        loop={shouldLoop}
        muted
        playsInline
        poster={shouldLoadMedia ? thumbnailUrl : ''}
        preload={shouldPreload}
        width="100%"
      >
        {sources?.map(source => (
          <source src={source.src} key={source.src} type={source.type} />
        ))}
      </video>
    </div>
  );
}
